import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/Components/ui/dialog';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { Text } from '@/Components/ui/typography';
import { getApiUrl } from '@/config/api';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';

const OTPVerificationPopup = ({ 
  isOpen, 
  onClose, 
  email, 
  onVerificationSuccess, 
  onResendOTP 
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  // Countdown timer for resend button
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Focus first input when popup opens
  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [isOpen]);

  const handleOTPChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(0, 1);
    
    if (digit) {
      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);
      setErrorMessage('');

      // Auto-focus next input
      if (index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (otp[index]) {
        // If current field has a digit, clear it
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        // If current field is empty, move to previous field and clear it
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1].focus();
      }
    }
    
    // Handle delete key
    if (e.key === 'Delete') {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
    }
    
    // Handle arrow keys for navigation
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');
    const newOtp = [...otp];
    digits.forEach((digit, i) => {
      if (i < 6) newOtp[i] = digit;
    });
    setOtp(newOtp);
    const nextIndex = Math.min(digits.length, 5);
    if (inputRefs.current[nextIndex]) {
      inputRefs.current[nextIndex].focus();
    }
  };

  const getOTPString = () => otp.join('');

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const otpString = getOTPString();
    if (otpString.length !== 6) {
      setErrorMessage('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifying(true);
    setErrorMessage('');

    try {
      const response = await fetch(getApiUrl('/api/verify_otp'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session management
        body: JSON.stringify({ otp: otpString, email: email.trim().toLowerCase() }),
      });

      const data = await response.json();

      if (data.verified) {
        setSuccessMessage('Email verified successfully! Creating your account...');
        setIsCreatingAccount(true);
        setTimeout(() => {
          onVerificationSuccess();
        }, 1500);
      } else {
        setErrorMessage(data.error || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;

    setIsResending(true);
    setErrorMessage('');

    try {
      const response = await fetch(getApiUrl('/api/send_otp'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for session management
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('OTP sent successfully!');
        setCountdown(60); // 60 seconds countdown
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage(data.error || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open && !isVerifying && !isResending && !isCreatingAccount) {
          onClose();
        }
      }}
    >
      <DialogContent 
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => {
          if (isVerifying || isResending || isCreatingAccount) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (isVerifying || isResending || isCreatingAccount) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl font-semibold text-center">
            Verify Your Email
          </DialogTitle>
          <DialogDescription className="text-center">
            We've sent a 6-digit verification code to
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">

          {/* Email Display */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/50 border border-border">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <Text size="base" className="font-semibold text-foreground break-all">
                {email}
              </Text>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            {/* OTP Input Boxes */}
            <div className="flex justify-center gap-2.5 sm:gap-3">
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  value={digit}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  maxLength="1"
                  disabled={isVerifying || isCreatingAccount}
                  autoComplete="one-time-code"
                  className={cn(
                    "w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl sm:text-3xl font-bold",
                    "border-2 transition-colors duration-200",
                    "focus:border-primary focus:ring-2 focus:ring-primary/20",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    digit 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-primary/30",
                    "placeholder:text-transparent"
                  )}
                />
              ))}
            </div>

            {/* Messages */}
            {errorMessage && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/30 p-3">
                <div className="flex items-start gap-2">
                  <X className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <Text size="sm" variant="destructive" className="text-destructive flex-1">
                    {errorMessage}
                  </Text>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="rounded-lg bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 p-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <Text size="sm" className="text-green-800 dark:text-green-200 flex-1">
                    {successMessage}
                  </Text>
                </div>
              </div>
            )}

            {/* Verify Button */}
            <Button 
              type="submit" 
              variant="default"
              size="lg"
              className="w-full h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
              style={{
                backgroundColor: getColor('primary'),
                color: getColor('primaryForeground'),
                borderColor: getColor('primary')
              }}
              disabled={getOTPString().length !== 6 || isVerifying || isCreatingAccount}
            >
              {isVerifying ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Verifying...
                </span>
              ) : isCreatingAccount ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Creating Account...
                </span>
              ) : (
                'Verify Email'
              )}
            </Button>
          </form>

          {/* Resend Section */}
          <div className="pt-4 border-t border-border">
            <div className="text-center space-y-2">
              <Text size="sm" variant="muted" className="text-muted-foreground">
                Didn't receive the code?
              </Text>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleResendOTP}
                disabled={isResending || countdown > 0}
                className="h-auto p-0 font-semibold"
              >
                {isResending ? (
                  <span className="flex items-center gap-2">
                    <span className="h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : countdown > 0 ? (
                  `Resend in ${countdown}s`
                ) : (
                  'Resend Code'
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OTPVerificationPopup;
